// -----------------------------------------------------------------------------
// STOCKPRO V4 — Firebase Auth + Firestore + Soft Delete
// VisualisPro — visualispro.be
//
// INSTRUCTIONS D'INSTALLATION :
// 1. npm install firebase
// 2. Crée un projet Firebase sur console.firebase.google.com
// 3. Active Authentication (Email/Password)
// 4. Active Firestore Database
// 5. Copie tes clés Firebase dans FIREBASE_CONFIG ci-dessous
// 6. Copie firestore.rules dans la console Firebase
// -----------------------------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

// --- Modules internes --------------------------------------------------------
import {
  db,
  PRODUCTS_COL,
  HISTORY_COL,
} from "./firebase";
import { T } from "./i18n/translations";
import { daysUntil } from "./utils/date";
import { getFirestoreErrorMessage, logFirestoreError } from "./utils/firestoreErrors";
import { validateProductForm } from "./utils/validation";
import { buildCSS, getColors, DEFAULT_ACCENT } from "./styles/theme";
import { useAuth } from "./hooks/useAuth";
import { useProducts } from "./hooks/useProducts";
import { useHistory } from "./hooks/useHistory";
import { useLosses } from "./hooks/useLosses";
import { Ic, ICONS } from "./components/Icons";
import Login from "./components/Login";
import LoadingScreen from "./components/LoadingScreen";
import Sidebar from "./components/Sidebar";
import ProductFormModal from "./components/ProductFormModal";
import DuplicateProductModal from "./components/DuplicateProductModal";
import LossReasonModal from "./components/LossReasonModal";
import ConfirmDeleteModal from "./components/ConfirmDeleteModal";
import ScanModal from "./components/ScanModal";
import DashboardPage from "./components/pages/DashboardPage";
import ProductsPage from "./components/pages/ProductsPage";
import AlertsPage from "./components/pages/AlertsPage";
import HistoryPage from "./components/pages/HistoryPage";
import InventoryPage from "./components/pages/InventoryPage";
import LicencePage from "./components/pages/LicencePage";
import ContactPage from "./components/pages/ContactPage";
import SettingsPage from "./components/pages/SettingsPage";

// --- Main App -----------------------------------------------------------------
export default function App() {
  const [dark, setDark]     = useState(true);
  const [lang, setLang]     = useState("fr");
  const { authUser, userProfile, loading, licenceExpired, logout, resetPassword, restaurantId } = useAuth();
  const { products, error: productsError } = useProducts(restaurantId);
  const { historyLogs } = useHistory(restaurantId);
  const { pertesLogs } = useLosses(restaurantId);
  const [view, setView]     = useState("dashboard");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("Tous");
  const [zoneFilter, setZoneFilter] = useState("Tous");
  const [showAdd, setShowAdd]   = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [duplicateProduct, setDuplicateProduct] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast]   = useState(null);
  const [showScan, setShowScan] = useState(false);
  const [inventoryMode, setInventoryMode] = useState(false);
  const [inventoryCounts, setInventoryCounts] = useState({});
  const [pertesFilter, setPertesFilter] = useState("Tous");
  const [historyTab, setHistoryTab] = useState("mouvements");
  const [showLossModal, setShowLossModal] = useState(null);
  const [syncStatus, setSyncStatus] = useState("synced"); // synced | saving | error
  const [online, setOnline] = useState(navigator.onLine);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = window.innerWidth < 768;
  const [form, setForm]     = useState({ name:"", cat:"Autre", unit:"pcs", qty:"", alert:"5", price:"", sellPrice:"", zone:"Réserve sèche", dlc:"" });
  const [foodCostThresholds, setFoodCostThresholds] = useState(() => {
    try { const s = localStorage.getItem('fc_thresholds'); return s ? JSON.parse(s) : { green:30, orange:35 }; } catch { return { green:30, orange:35 }; }
  });
  const scannerRef = useRef(null);
  const scanContainerRef = useRef(null);

  const t      = T[lang];
  const isRtl  = lang === "ar";
  const accent = userProfile?.color || DEFAULT_ACCENT;
  const isStaff = userProfile?.role === "staff";
  const { textPrimary, textSecondary, borderColor } = getColors(dark);

  // -- Food Cost helpers --
  const fcColor = (fc) => fc <= foodCostThresholds.green ? "#10b981" : fc <= foodCostThresholds.orange ? "#f59e0b" : "#ef4444";
  const fcBg    = (fc) => fc <= foodCostThresholds.green ? "rgba(16,185,129,.1)" : fc <= foodCostThresholds.orange ? "rgba(245,158,11,.1)" : "rgba(239,68,68,.1)";
  const fcLabel = (fc) => fc <= foodCostThresholds.green ? "🟢" : fc <= foodCostThresholds.orange ? "🟡" : "🔴";
  const saveFcThresholds = (val) => { setFoodCostThresholds(val); try { localStorage.setItem('fc_thresholds', JSON.stringify(val)); } catch {} };

  // -- Toast --
  const toastTimerRef = useRef(null);
  const toast_ = useCallback((msg, type = "success") => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast({ msg, type });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 3000);
  }, []);

  // -- Online / Offline --
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // -- Réaction aux erreurs Firestore -----------------------------------------
  useEffect(() => {
    if (productsError) {
      logFirestoreError("useProducts", productsError);
      setSyncStatus("error");
      toast_(getFirestoreErrorMessage(productsError), "error");
    }
  }, [productsError, toast_]);

  // -- Logout --
  const handleLogout = async () => {
    await logout();
    setView("dashboard");
  };

  // -- Loading state --
  if (loading) return <LoadingScreen dark={dark} />;
  if (!authUser) return <Login lang={lang} setLang={setLang} dark={dark} setDark={setDark} />;

  if (licenceExpired) return (
    <div style={{ minHeight:"100vh", background:dark?"#070711":"#f0f2f8", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <style>{buildCSS(dark)}</style>
      <div className="fade-up" style={{ maxWidth:400, width:"100%", textAlign:"center" }}>
        <div style={{ width:64, height:64, background:"rgba(239,68,68,.1)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
          <Ic d={ICONS.lock} size={32} color="#ef4444" />
        </div>
        <h2 style={{ fontSize:22, fontWeight:800, color:dark?"#fff":"#1e293b", marginBottom:8 }}>Licence expirée</h2>
        <p style={{ color:dark?"#666":"#888", fontSize:14, marginBottom:24 }}>
          Votre licence StockPro a expiré. Contactez VisualisPro pour renouveler votre abonnement et récupérer l'accès à vos données.
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <a href="https://wa.me/32465952862?text=Bonjour+VisualisPro,+ma+licence+StockPro+a+expiré,+je+voudrais+renouveler." target="_blank" rel="noreferrer"
            style={{ padding:"13px", borderRadius:10, background:"#25D366", color:"#fff", fontWeight:700, fontSize:15, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            <Ic d={ICONS.phone} size={18} color="#fff" /> WhatsApp VisualisPro
          </a>
          <a href="mailto:contact@visualispro.be?subject=Renouvellement licence StockPro"
            style={{ padding:"13px", borderRadius:10, background:dark?"rgba(255,255,255,.08)":"rgba(0,0,0,.06)", color:dark?"#fff":"#1e293b", fontWeight:700, fontSize:15, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:8, border:`1px solid ${dark?"rgba(255,255,255,.1)":"rgba(0,0,0,.1)"}` }}>
            <Ic d={ICONS.mail} size={18} /> contact@visualispro.be
          </a>
          <button onClick={handleLogout} style={{ padding:"10px", borderRadius:10, background:"none", color:dark?"#444":"#bbb", fontSize:13, border:"none", cursor:"pointer" }}>
            Se déconnecter
          </button>
        </div>
        <div style={{ marginTop:20, fontSize:11, color:dark?"#333":"#ccc" }}>StockPro by VisualisPro · visualispro.be</div>
      </div>
    </div>
  );
  // -- Stats --
  const alerts      = products.filter(p => p.qty <= p.alert);
  const dlcAlerts   = products.filter(p => { const d = daysUntil(p.dlc); return d !== null && d <= 3; });
  const totalAlerts = [...new Set([...alerts.map(p=>p.id), ...dlcAlerts.map(p=>p.id)])].length;
  const totalValue  = products.reduce((s,p) => s + (p.qty || 0) * (p.price || 0), 0);
  const usedCats    = [...new Set(products.map(p=>p.cat))];
  const usedZones   = [...new Set(products.map(p=>p.zone).filter(Boolean))];

  const filtered = products.filter(p => {
    const ms = p.name?.toLowerCase().includes(search.toLowerCase()) || p.cat?.toLowerCase().includes(search.toLowerCase());
    const mc = catFilter === "Tous" || p.cat === catFilter;
    const mz = zoneFilter === "Tous" || p.zone === zoneFilter;
    return ms && mc && mz;
  });

  // -- Ajouter un log dans l'historique Firestore --
  // Note: ne lance pas de toast en cas d'erreur (erreur silencieuse car
  // c'est un log secondaire — si Firestore est down, on a déjà un toast principal)
  const addHistory = async (productId, productName, action, before, after, unit) => {
    try {
      await addDoc(collection(db, HISTORY_COL), {
        userId:      authUser.uid,
        restaurantId: userProfile.restaurantId || authUser.uid,
        productId,
        productName,
        action,
        before,
        after,
        unit:        unit || "",
        createdAt:   serverTimestamp(),
      });
    } catch (e) {
      logFirestoreError("addHistory", e, { productId, action });
    }
  };

  // -- Mettre à jour la quantité (+/-) --
  const updateQty = async (product, delta) => {
    const newQty = Math.max(0, (product.qty || 0) + delta);
    setSyncStatus("saving");
    try {
      await updateDoc(doc(db, PRODUCTS_COL, product.id), {
        qty:       newQty,
        updatedAt: serverTimestamp(),
      });
      await addHistory(
        product.id,
        product.name,
        delta > 0 ? `+${delta}` : `${delta}`,
        product.qty,
        newQty,
        product.unit
      );
      setSyncStatus("synced");
      toast_(t.updatedOk);
    } catch (e) {
      logFirestoreError("updateQty", e, { productId: product.id, delta });
      toast_(getFirestoreErrorMessage(e), "error");
      setSyncStatus("error");
    }
  };

  // -- SOFT DELETE — updateDoc isDeleted: true --
  // ??  JAMAIS de deleteDoc() — la donnée reste dans Firestore, juste masquée
  const softDeleteProduct = async (product) => {
    setSyncStatus("saving");
    try {
      await updateDoc(doc(db, PRODUCTS_COL, product.id), {
        isDeleted:  true,
        deletedAt:  serverTimestamp(), // Pour traçabilité et restauration
      });
      await addHistory(
        product.id,
        product.name,
        "SUPPRESSION (soft)",
        product.qty,
        0,
        product.unit
      );
      setSyncStatus("synced");
      toast_(t.deletedOk);
    } catch (e) {
      logFirestoreError("softDeleteProduct", e, { productId: product.id });
      toast_(getFirestoreErrorMessage(e), "error");
      setSyncStatus("error");
    }
    setConfirmDel(null);
  };

  // -- Ajouter ou modifier un produit --
 const saveProduct = async () => {
    // Validation côté client
    const validation = validateProductForm(form);
    if (!validation.valid) {
      return toast_(validation.error, "error");
    }

    // 🆕 DÉTECTION DE DOUBLON (uniquement en mode CRÉATION, pas en édition)
    if (!editItem) {
      const trimmedName = form.name.trim().toLowerCase();
      const existing = products.find(
        (p) => p.name && p.name.trim().toLowerCase() === trimmedName && !p.isDeleted
      );
      if (existing) {
        setDuplicateProduct(existing);
        setShowDuplicate(true);
        return;
      }
    }

    setSyncStatus("saving");

    const payload = {
      userId:    authUser.uid,         // OBLIGATOIRE — sécurité multi-tenant
      restaurantId: userProfile.restaurantId || authUser.uid,
      name:      form.name.trim(),
      cat:       form.cat,
      unit:      form.unit,
      qty:       parseFloat(form.qty) || 0,
      alert:     parseFloat(form.alert) || 5,
      price:     parseFloat(form.price) || 0,
      sellPrice: parseFloat(form.sellPrice) || 0,
      zone:      form.zone,
      dlc:       form.dlc || "",
      isDeleted: false,                // OBLIGATOIRE — Soft Delete
      updatedAt: serverTimestamp(),
    };

    try {
      if (editItem) {
        // Modification
        await updateDoc(doc(db, PRODUCTS_COL, editItem.id), payload);
        await addHistory(editItem.id, form.name, "MODIFICATION", editItem.qty, parseFloat(form.qty), form.unit);
        toast_(t.updatedOk);
      } else {
        // Création — ajout de createdAt
        await addDoc(collection(db, PRODUCTS_COL), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        toast_(t.addedOk);
      }
      setShowAdd(false);
      setEditItem(null);
      setForm({ name:"", cat:"Autre", unit:"pcs", qty:"", alert:"5", price:"", zone:"Réserve sèche", dlc:"" });
      setSyncStatus("synced");
    } catch (e) {
      logFirestoreError("saveProduct", e, { editing: !!editItem, name: form.name });
      toast_(getFirestoreErrorMessage(e), "error");
      setSyncStatus("error");
    }
  };

  const openEdit = (p) => {
    setEditItem(p);
    setForm({ name:p.name, cat:p.cat, unit:p.unit, qty:String(p.qty), alert:String(p.alert), price:String(p.price||0), sellPrice:String(p.sellPrice||0), zone:p.zone||"Réserve sèche", dlc:p.dlc||"" });
    setShowAdd(true);
  };

// 🆕 GESTION DU MODAL DE DOUBLON

// Option 1 : Mettre à jour le produit existant (ajouter la quantité au stock actuel)
const handleUpdateExisting = async () => {
  if (!duplicateProduct) return;
  const newTotalQty =
    (parseFloat(duplicateProduct.qty) || 0) + (parseFloat(form.qty) || 0);
  try {
    await updateDoc(doc(db, PRODUCTS_COL, duplicateProduct.id), {
      qty: newTotalQty,
      updatedAt: serverTimestamp(),
    });
    setShowDuplicate(false);
    setDuplicateProduct(null);
    setShowAdd(false);
    setEditItem(null);
    setForm({ name:"", cat:"Autre", unit:"pcs", qty:"", alert:"5", price:"", zone:"Réserve sèche", dlc:"" });
    toast_(t.updatedOk);
    setSyncStatus("synced");
  } catch (err) {
    logFirestoreError("handleUpdateExisting", err);
    toast_(getFirestoreErrorMessage(err), "error");
    setSyncStatus("error");
  }
};

// Option 2 : Modifier le nom (ferme juste le modal de doublon, garde le formulaire ouvert)
const handleRename = () => {
  setShowDuplicate(false);
  setDuplicateProduct(null);
  // Le formulaire d'ajout reste ouvert pour que l'utilisateur change le nom
};

// Option 3 : Annuler tout
const handleCancelDuplicate = () => {
  setShowDuplicate(false);
  setDuplicateProduct(null);
};

  // -- Appliquer l'inventaire --
  const addLoss = async (product, reason) => {
    const newQty = Math.max(0, (product.qty || 0) - 1);
    setSyncStatus("saving");
    try {
      await updateDoc(doc(db, PRODUCTS_COL, product.id), {
        qty: newQty,
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "pertes_logs"), {
        userId: authUser.uid,
        restaurantId: userProfile.restaurantId || authUser.uid,
        productId: product.id,
        productName: product.name,
        quantity: 1,
        reason: reason,
        createdAt: serverTimestamp(),
      });
      await addHistory(product.id, product.name, `${reason} -1`, product.qty, newQty, product.unit);
      setSyncStatus("synced");
      toast_(`${reason} enregistré ✓`);
    } catch(e) {
      logFirestoreError("addLoss", e, { productId: product.id, reason });
      toast_(getFirestoreErrorMessage(e), "error");
      setSyncStatus("error");
    }
    setShowLossModal(null);
  };
  const applyInventory = async () => {
    setSyncStatus("saving");
    const promises = [];
    for (const p of products) {
      const count = inventoryCounts[p.id];
      if (count === undefined || count === "") continue;
      const newQty = parseFloat(count);
      if (newQty === p.qty) continue;
      promises.push(
        updateDoc(doc(db, PRODUCTS_COL, p.id), { qty: newQty, updatedAt: serverTimestamp() })
          .then(() => addHistory(p.id, p.name, `Inventaire ${newQty-p.qty>=0?"+":""}${newQty-p.qty}`, p.qty, newQty, p.unit))
      );
    }
    try {
      await Promise.all(promises);
      setSyncStatus("synced");
      toast_(t.inventoryApplied);
    } catch (e) {
      logFirestoreError("applyInventory", e);
      setSyncStatus("error");
      toast_(getFirestoreErrorMessage(e), "error");
    }
    setInventoryMode(false);
    setInventoryCounts({});
  };

  // -- Export liste de courses --
const exportCSV = () => {
    const headers = ["Nom", "Catégorie", "Zone", "Quantité", "Unité", "Prix unitaire (€)", "Valeur totale (€)"];
    const rows = products.map(p => [
      p.name,
      p.cat,
      p.zone || "-",
      p.qty,
      p.unit,
      (p.price || 0).toFixed(2),
      ((p.qty || 0) * (p.price || 0)).toFixed(2)
    ]);
    const csv = [headers, ...rows].map(r => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type:"text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventaire-${(userProfile?.name||"stock").replace(/\s/g,"-")}-${new Date().toLocaleDateString("fr-BE").replace(/\//g,"-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast_("Inventaire exporté en CSV ✓");
  };
const exportList = () => {
    const low = products.filter(p => p.qty <= p.alert);
    if (!low.length) return toast_(t.noAlerts, "error");
    
    import("jspdf").then(({ jsPDF }) => {
      const doc = new jsPDF();
      const accent = [255, 107, 53];
      
      // Header
      doc.setFillColor(...accent);
      doc.rect(0, 0, 210, 35, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.text("StockPro", 14, 16);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text("Liste de courses — " + (userProfile?.name || authUser.email), 14, 26);
      doc.text(new Date().toLocaleDateString("fr-BE"), 160, 26);
      
      // Produits
      let y = 50;
      doc.setTextColor(30, 41, 59);
      low.forEach((p, i) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFillColor(i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 250 : 255, i % 2 === 0 ? 252 : 255);
        doc.rect(10, y - 6, 190, 12, "F");
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.text(p.name, 14, y);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Stock: ${p.qty} ${p.unit} (min: ${p.alert})`, 100, y);
        doc.text(p.zone || "-", 170, y);
        y += 14;
      });
      
      // Footer
      doc.setFillColor(...accent);
      doc.rect(0, 285, 210, 15, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text("StockPro by VisualisPro · visualispro.be", 14, 293);
      
      doc.save(`courses-${(userProfile?.name || "stock").replace(/\s/g, "-")}.pdf`);
      toast_(t.exportedOk);
    });
  };

  // -- Scan code-barres --
  const startScan = async () => {
    setShowScan(true);
    setTimeout(async () => {
      if (!scanContainerRef.current) return;
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        const scanner = new Html5Qrcode("scan-container");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps:10, qrbox:{ width:250, height:150 } },
          (code) => {
            const found = products.find(p => p.name.toLowerCase().includes(code.toLowerCase()));
            if (found) { toast_(t.productFound + " — " + found.name); updateQty(found, 1); }
            else toast_(t.productNotFound + ": " + code, "error");
            stopScan();
          }, () => {}
        );
      } catch { toast_("Caméra non disponible", "error"); setShowScan(false); }
    }, 300);
  };

  const stopScan = async () => {
    if (scannerRef.current) { try { await scannerRef.current.stop(); } catch {} scannerRef.current = null; }
    setShowScan(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:dark?"#070711":"#f0f2f8", fontFamily:"'Outfit',sans-serif", color:textPrimary, display:"flex" }} dir={isRtl?"rtl":"ltr"}>
      <style>{buildCSS(dark)}</style>

      {/* -- Sidebar -- */}
      <Sidebar
        lang={lang}
        dark={dark}
        accent={accent}
        authUser={authUser}
        userProfile={userProfile}
        isStaff={isStaff}
        totalAlerts={totalAlerts}
        syncStatus={syncStatus}
        online={online}
        view={view}
        setView={setView}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isMobile={isMobile}
        onLogout={handleLogout}
        onExportList={exportList}
      />

      {/* -- Main Content -- */}
      <div style={{ flex:1, overflow:"auto", padding:"22px 20px", paddingTop: isMobile ? "60px" : "22px" }}>

        {/* - Dashboard - */}
        {view==="dashboard" && (
          <DashboardPage
            lang={lang}
            dark={dark}
            accent={accent}
            authUser={authUser}
            userProfile={userProfile}
            isStaff={isStaff}
            products={products}
            alerts={alerts}
            dlcAlerts={dlcAlerts}
            totalAlerts={totalAlerts}
            totalValue={totalValue}
            usedCats={usedCats}
            fcColor={fcColor}
            fcBg={fcBg}
            fcLabel={fcLabel}
          />
        )}
        {view==="products" && (
          <ProductsPage
            lang={lang}
            dark={dark}
            accent={accent}
            isMobile={isMobile}
            isStaff={isStaff}
            products={products}
            filtered={filtered}
            totalValue={totalValue}
            usedCats={usedCats}
            usedZones={usedZones}
            search={search}
            setSearch={setSearch}
            catFilter={catFilter}
            setCatFilter={setCatFilter}
            zoneFilter={zoneFilter}
            setZoneFilter={setZoneFilter}
            fcColor={fcColor}
            fcLabel={fcLabel}
            onScan={startScan}
            onAddNew={() => { setEditItem(null); setForm({name:"",cat:"Autre",unit:"pcs",qty:"",alert:"5",price:"",zone:"Réserve sèche",dlc:""}); setShowAdd(true); }}
            onEdit={openEdit}
            onDelete={setConfirmDel}
            onIncrement={(p) => updateQty(p, 1)}
            onDecrement={(p) => setShowLossModal(p)}
          />
        )}

        {/* - Alerts - */}
        {view==="alerts" && (
          <AlertsPage
            lang={lang}
            dark={dark}
            alerts={alerts}
            dlcAlerts={dlcAlerts}
            totalAlerts={totalAlerts}
            onExportList={exportList}
          />
        )}

        {/* - History - */}
        {view==="history" && (
          <HistoryPage
            lang={lang}
            dark={dark}
            accent={accent}
            historyLogs={historyLogs}
            pertesLogs={pertesLogs}
            historyTab={historyTab}
            setHistoryTab={setHistoryTab}
            pertesFilter={pertesFilter}
            setPertesFilter={setPertesFilter}
          />
        )}

        {/* - Inventory - */}
        {view==="inventory" && (
          <InventoryPage
            lang={lang}
            dark={dark}
            accent={accent}
            products={products}
            inventoryMode={inventoryMode}
            setInventoryMode={setInventoryMode}
            inventoryCounts={inventoryCounts}
            setInventoryCounts={setInventoryCounts}
            onApply={applyInventory}
            onExportCSV={exportCSV}
          />
        )}

        {view==="licence" && (
          <LicencePage
            lang={lang}
            dark={dark}
            accent={accent}
            authUser={authUser}
            userProfile={userProfile}
          />
        )}

        {/* - Contact - */}
        {view==="contact" && (
          <ContactPage
            lang={lang}
            dark={dark}
            accent={accent}
          />
        )}

        {/* - Settings - */}
        {view==="settings" && (
          <SettingsPage
            lang={lang}
            setLang={setLang}
            dark={dark}
            setDark={setDark}
            accent={accent}
            authUser={authUser}
            isStaff={isStaff}
            foodCostThresholds={foodCostThresholds}
            saveFcThresholds={saveFcThresholds}
            onResetPassword={async () => {
              try {
                await resetPassword(authUser.email);
                toast_("Email de réinitialisation envoyé ✓");
              } catch(e) {
                toast_("Erreur lors de l'envoi", "error");
              }
            }}
          />
        )}
      </div>{/* -- Modal Ajouter/Modifier -- */}
      {showAdd && (
        <ProductFormModal
          lang={lang}
          dark={dark}
          accent={accent}
          isStaff={isStaff}
          form={form}
          setForm={setForm}
          editItem={editItem}
          onSave={saveProduct}
          onClose={() => setShowAdd(false)}
        />
      )}

      {/* 🆕 Modal de détection de doublon */}
{showDuplicate && duplicateProduct && (
  <DuplicateProductModal
    dark={dark}
    accent={accent}
    existingProduct={duplicateProduct}
    newQty={form.qty}
    onUpdateExisting={handleUpdateExisting}
    onRename={handleRename}
    onCancel={handleCancelDuplicate}
  />
)}

      {/* -- Loss Modal -- */}
      {showLossModal && (
        <LossReasonModal
          product={showLossModal}
          dark={dark}
          onSale={(p) => updateQty(p, -1)}
          onLoss={addLoss}
          onClose={() => setShowLossModal(null)}
        />
      )}

      {/* -- Confirm Soft Delete -- */}
      {confirmDel && (
        <ConfirmDeleteModal
          product={confirmDel}
          lang={lang}
          dark={dark}
          onConfirm={softDeleteProduct}
          onClose={() => setConfirmDel(null)}
        />
      )}

      {/* -- Scan Modal -- */}
      {showScan && (
        <ScanModal
          lang={lang}
          dark={dark}
          scanContainerRef={scanContainerRef}
          onClose={stopScan}
        />
      )}

      {/* -- Toast -- */}
      {toast && (
        <div key={toast.msg + Date.now()} style={{ position:"fixed", bottom:18, right:18, padding:"10px 16px", background:toast.type==="error"?"rgba(239,68,68,.12)":"rgba(16,185,129,.12)", border:`1px solid ${toast.type==="error"?"rgba(239,68,68,.25)":"rgba(16,185,129,.25)"}`, borderRadius:10, color:toast.type==="error"?"#f87171":"#10b981", fontWeight:600, fontSize:13, animation:"toast .3s ease", zIndex:200, backdropFilter:"blur(10px)" }}>
          {toast.msg}
         </div>
       )}
         </div>
    );
  }
